import { api } from './api'
import {
  ClinicalHistory,
  ClinicalHistoryCreateRequest,
  ClinicalHistoryAddendumRequest,
  ClinicalHistoryNoteRequest,
  ClinicalSummary,
  Patient,
  PatientCreateRequest,
  PaginatedResponse,
  PatientSearchParams,
} from '@/types'

export const patientApi = {
  getPatients: async (params?: PatientSearchParams): Promise<PaginatedResponse<Patient>> => {
    const searchParams = new URLSearchParams()
    
    if (params?.query) searchParams.append('query', params.query)
    if (params?.first_name) searchParams.append('first_name', params.first_name)
    if (params?.last_name) searchParams.append('last_name', params.last_name)
    if (params?.status) searchParams.append('status', params.status)
    if (params?.skip !== undefined) searchParams.append('skip', params.skip.toString())
    if (params?.limit !== undefined) searchParams.append('limit', params.limit.toString())
    
    return api.get(`/patients?${searchParams.toString()}`)
  },
  
  getPatient: async (id: string): Promise<Patient> => {
    return api.get(`/patients/${id}`)
  },
  
  getPatientByMrn: async (mrn: string): Promise<Patient> => {
    return api.get(`/patients/mrn/${mrn}`)
  },
  
  createPatient: async (data: PatientCreateRequest): Promise<Patient> => {
    return api.post('/patients', data)
  },
  
  updatePatient: async (id: string, data: Partial<PatientCreateRequest>): Promise<Patient> => {
    return api.put(`/patients/${id}`, data)
  },
  
  deletePatient: async (id: string, reason?: string): Promise<void> => {
    return api.delete(`/patients/${id}?reason=${encodeURIComponent(reason || '')}`)
  },

  /** Append a new diagnosis summary entry. */
  appendDiagnosisSummary: async (patientId: string, text: string): Promise<Patient> => {
    return api.post(`/patients/${patientId}/diagnosis-summary`, { text })
  },

  /** Append a new current plan entry. */
  appendCurrentPlan: async (patientId: string, text: string): Promise<Patient> => {
    return api.post(`/patients/${patientId}/current-plan`, { text })
  },

  /** Sign and lock the NOM-004 identification card (closes the record). */
  signIdentityCard: async (patientId: string): Promise<Patient> => {
    return api.post(`/patients/${patientId}/identity-signature`, {})
  },

  /** Append a NOM-004 clarifying addendum to a signed identification card. */
  appendIdentityAddendum: async (
    patientId: string,
    payload: {
      field: string
      field_label?: string | null
      previous_value?: string | null
      new_value: string
      note?: string | null
    }
  ): Promise<Patient> => {
    return api.post(`/patients/${patientId}/identity-addendum`, payload)
  },

  /** Upload a patient photo and return the new signed URL. */
  uploadPhoto: async (patientId: string, file: File): Promise<{ photo_url: string }> => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post(`/patients/${patientId}/photo`, formData as unknown as Record<string, unknown>, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },

  /** Get a fresh signed URL for the patient's photo (re-generates if expired). */
  getPhotoUrl: async (patientId: string): Promise<{ photo_url: string }> => {
    return api.get(`/patients/${patientId}/photo-url`)
  },
  /** Get the read-only current clinical summary derived from signed notes. */
  getClinicalSummary: async (patientId: string): Promise<ClinicalSummary> => {
    return api.get(`/patients/${patientId}/clinical-summary`)
  },

  /** Get the active clinical history header for a patient. */
  getClinicalHistory: async (patientId: string): Promise<ClinicalHistory> => {
    return api.get(`/patients/${patientId}/clinical-history`)
  },

  /** Create the clinical history for a patient (as an editable draft). */
  createClinicalHistory: async (
    patientId: string,
    data: ClinicalHistoryCreateRequest = {}
  ): Promise<ClinicalHistory> => {
    return api.post(`/patients/${patientId}/clinical-history`, data)
  },

  /** Save draft intake content (only while status === 'draft'). */
  updateClinicalHistory: async (
    patientId: string,
    content: Record<string, unknown>
  ): Promise<ClinicalHistory> => {
    return api.patch(`/patients/${patientId}/clinical-history`, { content })
  },

  /** Sign and permanently lock the clinical history. */
  signClinicalHistory: async (patientId: string): Promise<ClinicalHistory> => {
    return api.post(`/patients/${patientId}/clinical-history/sign`)
  },

  /** Append a clarifying addendum to a signed clinical history. */
  addClinicalHistoryAddendum: async (
    patientId: string,
    data: ClinicalHistoryAddendumRequest
  ): Promise<ClinicalHistory> => {
    return api.post(`/patients/${patientId}/clinical-history/addenda`, data)
  },

  /** Append a signed follow-up note to a signed clinical history. */
  addClinicalHistoryNote: async (
    patientId: string,
    data: ClinicalHistoryNoteRequest
  ): Promise<ClinicalHistory> => {
    return api.post(`/patients/${patientId}/clinical-history/notes`, data)
  },
  
  getStatistics: async (): Promise<{
    total_active_patients: number
    new_patients_this_month: number
    timestamp: string
  }> => {
    return api.get('/patients/statistics')
  },
}
